/**
 * PatchReleases.js
 * Dashboard page showing new patch releases detected by autoFetcher.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const SEVERITY_COLOR = {
  critical: '#dc2626', high: '#ea580c', medium: '#d97706', low: '#16a34a'
};
const SEVERITY_BG = {
  critical: '#fee2e2', high: '#ffedd5', medium: '#fef3c7', low: '#dcfce7'
};
const CATEGORY_ICON = {
  os: '🪟', browser: '🌐', application: '📦', driver: '🔧', bios: '⚡', firmware: '💾'
};

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff  = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  return `${days}d ${hours % 24}h ago`;
}

function nextCheckIn(lastCheckDate) {
  if (!lastCheckDate) return 'unknown';
  const diff = new Date(lastCheckDate).getTime() + 6 * 3600000 - Date.now();
  if (diff <= 0) return 'due now';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m`;
}

function countdownTo(targetDate) {
  if (!targetDate) return 'unknown';
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return 'due now';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m`;
}

export default function PatchReleases() {
  const [releases, setReleases]             = useState([]);
  const [summary, setSummary]               = useState({});
  const [pendingDevices, setPending]        = useState([]);
  const [pendingSummary, setPendingSummary] = useState({});
  const [zeroDayAlerts, setZeroDayAlerts]   = useState([]);
  const [loading, setLoading]               = useState(true);
  const [fetching, setFetching]             = useState(false);
  const [fetchMsg, setFetchMsg]             = useState('');
  const [checkDoneAt, setCheckDoneAt]       = useState(null); // timestamp of last manual check
  const [lastChecked, setLastChecked]       = useState(null);
  const [nextFetchAt, setNextFetchAt]       = useState(null);
  const [tab, setTab]                       = useState('releases');
  const [filter, setFilter]                 = useState('all');

  // Tick every minute to keep relative times fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [relRes, pendRes, statusRes] = await Promise.all([
        api.getPatchReleases(),
        api.getPendingDevices(),
        api.getFetcherStatus().catch(() => ({ data: null }))
      ]);
      const rels = relRes.data.releases || [];
      setReleases(rels);
      setSummary(relRes.data.summary || {});
      setZeroDayAlerts(relRes.data.zeroDayAlerts || []);
      setPending(pendRes.data.devices || []);
      setPendingSummary(pendRes.data.summary || {});

      // Use real fetcher state file if available, fall back to latest release
      if (statusRes.data?.lastFetchAt) {
        setLastChecked(statusRes.data.lastFetchAt);
        setNextFetchAt(statusRes.data.nextFetchAt);
      } else if (rels.length > 0) {
        const latest = rels.reduce((a, b) =>
          new Date(a.createdAt) > new Date(b.createdAt) ? a : b
        );
        setLastChecked(latest.createdAt);
        setNextFetchAt(null);
      }
    } catch (e) {
      console.error('Load error:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerFetch = async () => {
    setFetching(true);
    setFetchMsg('Checking all vendors for new releases...');
    setCheckDoneAt(null);
    const preFetchTime = new Date().toISOString();
    try {
      // Wait for completion — endpoint is now synchronous (REQ-3.1)
      const res = await api.triggerReleaseFetch();
      const { lastFetchAt, nextFetchAt: nfa, summary } = res.data;

      // Update status bar immediately with server-side completion time (REQ-2.1, REQ-1.3)
      setLastChecked(lastFetchAt);
      setNextFetchAt(nfa);
      setCheckDoneAt(lastFetchAt);

      const msg = summary
        ? `✔ Done — ${summary.updated} updated, ${summary.added} added, ${summary.current} current`
        : '✔ Check complete';
      setFetchMsg(msg);

      // Reload release table
      await load();

      // Clear the done message after 10 seconds (REQ-1.2)
      setTimeout(() => { setFetchMsg(''); setCheckDoneAt(null); }, 10000);
    } catch (e) {
      setFetchMsg('Error: ' + e.message);
      setTimeout(() => setFetchMsg(''), 5000);
    } finally {
      setFetching(false);
    }
  };

  const filteredReleases = releases.filter(r => {
    if (filter === 'critical') return r.severity === 'critical';
    if (filter === 'high')     return r.severity === 'high' || r.severity === 'critical';
    if (filter === 'new')      return r.releaseType === 'new';
    if (filter === 'updated')  return r.releaseType === 'updated';
    return true;
  });

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Patch Releases</div>
          <div className="page-subtitle">
            New patch versions detected from vendor sources · Auto-checked every 6 hours
          </div>
        </div>
        <div className="flex-gap">
          {fetchMsg && (
            <span style={{ fontSize: 12, color: '#d97706', background: '#fef3c7',
              padding: '6px 12px', borderRadius: 6, border: '1px solid #fde68a' }}>
              ⏳ {fetchMsg}
            </span>
          )}
          <button className="btn btn-ghost" onClick={load} disabled={loading}>↻ Refresh</button>
          <button className="btn btn-primary" onClick={triggerFetch} disabled={fetching}
            style={{ minWidth: 160 }}>
            {fetching
              ? '⏳ Checking...'
              : checkDoneAt
                ? `✔ Checked ${fmtDateTime(checkDoneAt).split(',')[1]?.trim() || 'just now'}`
                : '🔄 Check Now'}
          </button>
        </div>
      </div>

      {/* Auto-check status bar */}
      <div className="panel" style={{ marginBottom: 20 }}>
        <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>

            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase',
                letterSpacing: '0.6px', fontWeight: 600, marginBottom: 3 }}>Last Checked</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e2a3b' }}>
                {lastChecked ? fmtDateTime(lastChecked) : '—'}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                {lastChecked ? timeAgo(lastChecked) : 'Never run'}
              </div>
            </div>

            <div style={{ width: 1, background: '#e8ecf4', height: 40 }} />

            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase',
                letterSpacing: '0.6px', fontWeight: 600, marginBottom: 3 }}>Next Auto-Check</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1a56db' }}>
                {nextFetchAt
                  ? `in ${countdownTo(nextFetchAt)}`
                  : lastChecked ? `in ${nextCheckIn(lastChecked)}` : '—'}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>every 6 hours</div>
            </div>

            <div style={{ width: 1, background: '#e8ecf4', height: 40 }} />

            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase',
                letterSpacing: '0.6px', fontWeight: 600, marginBottom: 3 }}>Next Check At</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e2a3b' }}>
                {nextFetchAt
                  ? fmtDateTime(nextFetchAt)
                  : lastChecked
                    ? fmtDateTime(new Date(new Date(lastChecked).getTime() + 6 * 3600000))
                    : '—'}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>scheduled</div>
            </div>

          </div>
          <div className="flex-gap">
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e',
              boxShadow: '0 0 0 3px #dcfce7' }} />
            <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
              Auto-fetch active
            </span>
          </div>
        </div>
      </div>

      {/* ── Zero-Day Alert Banner ── */}
      {zeroDayAlerts.length > 0 && (
        <div style={{
          background: '#fff1f2', border: '2px solid #dc2626', borderRadius: 10,
          padding: '14px 20px', marginBottom: 20
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 22 }}>⚠️</span>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>
              Zero-Day Alert — {zeroDayAlerts.length} Critical Patch{zeroDayAlerts.length > 1 ? 'es' : ''} with No Devices Patched
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {zeroDayAlerts.map((z, i) => (
              <div key={i} style={{
                background: '#fee2e2', borderRadius: 8, padding: '8px 14px',
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: 18 }}>🔴</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e2a3b' }}>{z.patchName}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {z.vendor} · v{z.newVersion} · Detected: {fmtDateTime(z.detectedAt)}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, background: '#dc2626',
                  color: '#fff', padding: '3px 10px', borderRadius: 12 }}>
                  🚨 Zero-Day
                </span>
                <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>
                  {z.devicesQueued} device{z.devicesQueued !== 1 ? 's' : ''} queued
                </span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: '#dc2626' }}>
            ⚡ Deploy via Emergency Patches immediately to minimize exposure window.
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="cards-row" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 20 }}>
        {[
          { label: 'Total Releases',  value: summary.total    || 0, cls: 'blue',   icon: '📋', filterVal: 'all',      tabVal: 'releases' },
          { label: 'Critical',        value: summary.critical || 0, cls: 'red',    icon: '🔴', filterVal: 'critical', tabVal: 'releases' },
          { label: 'High',            value: summary.high     || 0, cls: 'amber',  icon: '🟠', filterVal: 'high',     tabVal: 'releases' },
          { label: 'Devices Pending', value: pendingSummary.devicesWithPending || 0, cls: 'purple', icon: '⏳', filterVal: null, tabVal: 'pending' },
        ].map(c => (
          <div key={c.label} className={`card ${c.cls}`}
            style={{ cursor: 'pointer', outline: filter === c.filterVal && c.filterVal !== 'all' ? '2px solid currentColor' : undefined }}
            onClick={() => { setTab(c.tabVal); if (c.filterVal) setFilter(filter === c.filterVal ? 'all' : c.filterVal); }}>
            <div className="card-label">{c.label}<span style={{ fontSize: 18 }}>{c.icon}</span></div>
            <div className="card-value">{c.value}</div>
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>
              {(c.filterVal && filter === c.filterVal) ? '▼ filtered' : 'click to filter'}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e8ecf4', marginBottom: 20 }}>
        {[
          { id: 'releases', label: `📋 Release History (${summary.total || 0})` },
          { id: 'pending',  label: `⏳ Pending Devices (${pendingSummary.devicesWithPending || 0})` }
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 20px', fontSize: 13, fontWeight: 600,
            color: tab === t.id ? '#1a56db' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #1a56db' : '2px solid transparent',
            marginBottom: -2, transition: 'all 0.15s'
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="loading">Loading releases...</div>

      : tab === 'releases' ? (
        <>
          {/* Filter bar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            {[
              { id: 'all',      label: 'All' },
              { id: 'critical', label: '🔴 Critical' },
              { id: 'high',     label: '🟠 High+' },
              { id: 'new',      label: '✨ New' },
              { id: 'updated',  label: '🔄 Updated' },
            ].map(f => (
              <button key={f.id}
                className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setFilter(f.id)}>
                {f.label}
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>
              {filteredReleases.length} release{filteredReleases.length !== 1 ? 's' : ''}
            </span>
          </div>

          {filteredReleases.length === 0 ? (
            <div className="panel" style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1e2a3b' }}>No releases found</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6 }}>
                Click "Check Now" to scan all vendors for new patch versions
              </div>
            </div>
          ) : (
            <div className="panel">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}></th>
                    <th>Patch</th>
                    <th>Vendor / Product</th>
                    <th>Version Change</th>
                    <th>Severity</th>
                    <th>Type</th>
                    <th>Devices Queued</th>
                    <th>Detected At</th>
                    <th>Release Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReleases.map(r => (
                    <tr key={r.id} style={{
                      background: r.isZeroDay ? '#fff1f2' : undefined,
                      borderLeft: r.isZeroDay ? '3px solid #dc2626' : undefined
                    }}>
                      <td style={{ textAlign: 'center', fontSize: 18 }}>
                        {CATEGORY_ICON[r.category] || '📦'}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#1e2a3b' }}>
                          {r.patchName}
                        </div>
                        {r.notes && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{r.notes}</div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{r.vendor}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.product}</div>
                      </td>
                      <td>
                        {r.previousVersion ? (
                          <div className="flex-gap" style={{ gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#dc2626', textDecoration: 'line-through' }}>
                              {r.previousVersion}
                            </span>
                            <span style={{ color: '#94a3b8' }}>→</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                              {r.newVersion}
                            </span>
                          </div>
                        ) : (
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                            v{r.newVersion}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="badge" style={{
                          background: SEVERITY_BG[r.severity],
                          color: SEVERITY_COLOR[r.severity]
                        }}>
                          {r.severity}
                        </span>
                      </td>
                      <td>
                        <span className="badge" style={{
                          background: r.releaseType === 'new' ? '#dcfce7' : '#dbeafe',
                          color:      r.releaseType === 'new' ? '#16a34a' : '#1d4ed8'
                        }}>
                          {r.releaseType === 'new' ? '✨ New' : '🔄 Updated'}
                        </span>
                      </td>
                      <td>
                        {r.devicesQueued > 0 ? (
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#d97706' }}>
                            ⏳ {r.devicesQueued} device{r.devicesQueued !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: '#16a34a' }}>✔ all patched</span>
                        )}
                        {r.isZeroDay && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', marginTop: 2 }}>
                            ⚠ Zero-Day
                          </div>
                        )}
                      </td>
                      {/* Full timestamps */}
                      <td>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#1e2a3b', whiteSpace: 'nowrap' }}>
                          {fmtDateTime(r.createdAt)}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                          {timeAgo(r.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                          {fmtDate(r.releaseDate)}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                          {timeAgo(r.releaseDate)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>

      ) : (
        /* Pending Devices Tab */
        <>
          {pendingDevices.length === 0 ? (
            <div className="panel" style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1e2a3b' }}>
                All devices are up to date
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6 }}>
                No pending patches across any device
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pendingDevices.map(d => (
                <div key={d.device?.id} className="panel">
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f4ff',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className="flex-gap">
                      <span style={{ fontSize: 20 }}>🖥️</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e2a3b' }}>
                          {d.device?.hostname}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          {d.device?.os} · {d.device?.osVersion} · {d.device?.ipAddress}
                        </div>
                      </div>
                    </div>
                    <div className="flex-gap">
                      {d.criticalCount > 0 && (
                        <span className="badge critical">{d.criticalCount} critical</span>
                      )}
                      {d.highCount > 0 && (
                        <span className="badge high">{d.highCount} high</span>
                      )}
                      <span className={`badge ${d.device?.status === 'online' ? 'online' : 'offline'}`}>
                        {d.device?.status === 'online' ? '● Online' : '○ Offline'}
                      </span>
                      <span className="badge pending">
                        {d.pendingPatches.length} pending
                      </span>
                    </div>
                  </div>
                  <div style={{ padding: '12px 20px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {d.pendingPatches.map((p, i) => (
                      <div key={i} style={{
                        background: '#f8fafc', border: '1px solid #e8ecf4',
                        borderLeft: `3px solid ${SEVERITY_COLOR[p.severity] || '#94a3b8'}`,
                        borderRadius: 6, padding: '6px 12px', fontSize: 12,
                        display: 'flex', alignItems: 'center', gap: 6
                      }}>
                        <span>{CATEGORY_ICON[p.category] || '📦'}</span>
                        <span style={{ fontWeight: 600, color: '#1e2a3b' }}>{p.patchName}</span>
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>v{p.version}</span>
                        {p.queuedAt && (
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>
                            · queued {timeAgo(p.queuedAt)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
